import React, { useState, useEffect } from "react";
import { Modal, ModalHeader, ModalBody, Form, FormGroup, Label, Input } from "reactstrap";
import { Button } from "../../../components/Component";
import http from "../../../utils/http-common";
import swal from "sweetalert";

/**
 * Quick create user for partner onboarding (role medical_partner). Used from MedicalPartner add flow.
 */
const CreatePartnerUserModal = ({ isOpen, onClose, onCreated }) => {
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [countryCode, setCountryCode] = useState("+33");
  const [salutation, setSalutation] = useState("Dr");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setEmail("");
      setFirstName("");
      setLastName("");
      setPhone("");
      setCountryCode("+33");
      setSalutation("Dr");
      setLoading(false);
    }
  }, [isOpen]);

  const submit = async (e) => {
    e.preventDefault();
    const em = email.trim();
    if (!em) {
      swal("Validation", "Email is required.", "warning");
      return;
    }
    setLoading(true);
    try {
      const res = await http.post("/api/v1/user", {
        email: em,
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        phone: phone.trim() || undefined,
        country_code: countryCode.trim() || undefined,
        salutation: salutation || undefined,
        role: "medical_partner",
      });
      const user = res.data?.data;
      if (user && onCreated) {
        onCreated(user);
      } else {
        swal("Error", "User created but response was unexpected.", "error");
      }
    } catch (err) {
      swal("Error", err.response?.data?.message || "Failed to create user", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={onClose} size="lg">
      <ModalHeader toggle={onClose}>Create user account (medical partner)</ModalHeader>
      <ModalBody>
        <p className="text-soft small">
          Creates a user with role <code>medical_partner</code>. A password setup email is sent when the API supports it.
        </p>
        <Form onSubmit={submit}>
          <FormGroup>
            <Label for="create-partner-user-email">Email *</Label>
            <Input
              id="create-partner-user-email"
              type="email"
              value={email}
              onChange={(ev) => setEmail(ev.target.value)}
              required
              autoComplete="email"
            />
          </FormGroup>
          <FormGroup>
            <Label for="create-partner-user-salutation">Salutation</Label>
            <Input
              id="create-partner-user-salutation"
              type="select"
              value={salutation}
              onChange={(ev) => setSalutation(ev.target.value)}
            >
              <option value="Dr">Dr</option>
              <option value="Mr">Mr</option>
              <option value="Mrs">Mrs</option>
              <option value="Ms">Ms</option>
            </Input>
          </FormGroup>
          <FormGroup>
            <Label for="create-partner-user-first">First name</Label>
            <Input
              id="create-partner-user-first"
              value={firstName}
              onChange={(ev) => setFirstName(ev.target.value)}
              autoComplete="given-name"
            />
          </FormGroup>
          <FormGroup>
            <Label for="create-partner-user-last">Last name</Label>
            <Input
              id="create-partner-user-last"
              value={lastName}
              onChange={(ev) => setLastName(ev.target.value)}
              autoComplete="family-name"
            />
          </FormGroup>
          <FormGroup>
            <Label for="create-partner-user-cc">Country code</Label>
            <Input
              id="create-partner-user-cc"
              value={countryCode}
              onChange={(ev) => setCountryCode(ev.target.value)}
              placeholder="+33"
            />
          </FormGroup>
          <FormGroup>
            <Label for="create-partner-user-phone">Phone</Label>
            <Input
              id="create-partner-user-phone"
              value={phone}
              onChange={(ev) => setPhone(ev.target.value)}
              autoComplete="tel"
            />
          </FormGroup>
          <div className="d-flex flex-g-2 justify-content-end">
            <Button type="button" color="light" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" color="primary" disabled={loading}>
              {loading ? "Creating…" : "Create user"}
            </Button>
          </div>
        </Form>
      </ModalBody>
    </Modal>
  );
};

export default CreatePartnerUserModal;
